import axios from 'axios';

// Use a mesma convenção do api.ts: a env já pode incluir /api/v1
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Configuração do axios para consultas (sem duplicar /api/v1)
const consultationApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
consultationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
consultationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Tipos para as consultas
export interface CardPosition {
  position: string;
  card_id: string;
  is_reversed: boolean;
}

export interface ConsultationRequest {
  question: string;
  spread_type: string;
  selected_cards: CardPosition[];
}

export interface CardInterpretation {
  card_id: string;
  card_name: string;
  position: string;
  meaning: string;
  interpretation: string;
  is_reversed: boolean;
  image_url?: string;
}

export interface FollowUpQA {
  question: string;
  answer: string;
  created_at?: string;
}

export interface ConsultationResponse {
  id: string;
  question: string;
  spread_type: string;
  spread_name: string;
  cards: CardInterpretation[];
  general_interpretation: string;
  advice: string;
  deeper_reading?: string | null;
  follow_up_qa?: FollowUpQA[] | null;
  created_at: string;
}

export interface ConsultationHistory {
  id: string;
  question: string;
  spread_type: string;
  spread_name: string;
  created_at: string;
  summary: string;
  // Campos adicionais para histórico completo
  cards: CardInterpretation[];
  general_interpretation: string;
  advice?: string;
}

export interface ConsultationListResponse {
  consultations: ConsultationHistory[];
  total: number;
}

// Serviços da API
export const consultationService = {
  // Criar nova consulta
  async createConsultation(consultationData: ConsultationRequest): Promise<ConsultationResponse> {
    try {
      const response = await consultationApi.post('/consultation/create', consultationData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar consulta');
    }
  },

  // Criar nova consulta de Baralho Cigano (mesmo shape do Tarot, endpoint próprio
  // — evita colidir IDs de carta cigana com os Arcanos Maiores do Tarot no backend)
  async createCiganoConsultation(consultationData: ConsultationRequest): Promise<ConsultationResponse> {
    try {
      const response = await consultationApi.post('/consultation/create-cigano', consultationData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar consulta');
    }
  },

  // Criar nova consulta de Baralho Sibila (mesmo shape do Tarot/Cigano, endpoint próprio
  // — evita colidir IDs de carta sibila com os outros dois decks no backend)
  async createSibillaConsultation(consultationData: ConsultationRequest): Promise<ConsultationResponse> {
    try {
      const response = await consultationApi.post('/consultation/create-sibilla', consultationData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar consulta');
    }
  },

  // Obter histórico de consultas
  async getConsultationHistory(skip: number = 0, limit: number = 20): Promise<ConsultationListResponse> {
    try {
      const response = await consultationApi.get(`/consultation/history?skip=${skip}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao buscar histórico');
    }
  },

  // Obter consulta específica
  async getConsultation(consultationId: string): Promise<ConsultationResponse> {
    try {
      const response = await consultationApi.get(`/consultation/${consultationId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao buscar consulta');
    }
  },

  // Aprofundar uma consulta existente (1 token)
  async deepenConsultation(consultationId: string): Promise<{ deeper_reading: string }> {
    try {
      const response = await consultationApi.post(`/consultation/${consultationId}/deepen`);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // Fazer uma pergunta de acompanhamento sobre uma consulta existente, usando a mesma tiragem (1 token)
  async askConsultation(consultationId: string, question: string): Promise<{ answer: string }> {
    try {
      const response = await consultationApi.post(`/consultation/${consultationId}/ask`, { question });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // Pergunta livre à Aurora Lyz
  async askAurora(question: string, skillType?: string): Promise<ConsultationResponse> {
    try {
      const response = await consultationApi.post('/consultation/aurora-question', {
        question,
        ...(skillType ? { skill_type: skillType } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao consultar Aurora Lyz');
    }
  },
};

export default consultationService;